import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from './Icon'
import { useCurrency } from '../lib/currency-context'
import type { Currency } from '../lib/money'
import { useLearnMode } from '../lib/learn-mode'
import { useAuth } from '../lib/auth'

const CURRENCIES: Currency[] = ['USD', 'CAD', 'GBP', 'EUR']

function UserMenu() {
  const { user, isAdmin, isOwner, logout } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref  = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (!user) return null

  const initials = user.name
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const roleBg: Record<string, string> = {
    owner: 'bg-plum-500',
    admin: 'bg-royal-500',
    member: 'bg-ink-400',
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 hover:bg-ink-50 rounded-xl px-2 py-1 transition-colors"
      >
        <div className={`w-8 h-8 rounded-full grid place-items-center text-white text-xs font-bold ${roleBg[user.role] ?? 'bg-ink-400'}`}>
          {initials}
        </div>
        <div className="hidden md:block text-left">
          <div className="text-[13px] font-medium text-ink-800 leading-tight">{user.name}</div>
          <div className="text-[11px] text-ink-400 capitalize">{user.role}</div>
        </div>
        <Icon name="chevron-down" className="w-3.5 h-3.5 text-ink-400 hidden md:block" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-52 bg-white rounded-xl shadow-lg border border-ink-200 py-1.5 z-50">
          <div className="px-3 py-2 border-b border-ink-100 mb-1">
            <div className="text-[13px] font-medium text-ink-900">{user.name}</div>
            <div className="text-[11px] text-ink-400">{user.email}</div>
            <span className={`inline-block mt-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full text-white ${roleBg[user.role] ?? 'bg-ink-400'}`}>
              {user.role}
            </span>
          </div>

          {isAdmin && (
            <button
              onClick={() => { setOpen(false); navigate('/users') }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-ink-700 hover:bg-ink-50 transition-colors"
            >
              <Icon name="users" className="w-4 h-4" />
              {t('user_menu.manage_users')}
            </button>
          )}

          <button
            onClick={() => { setOpen(false); navigate('/settings') }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-ink-700 hover:bg-ink-50 transition-colors"
          >
            <Icon name="user" className="w-4 h-4" />
            {t('user_menu.my_profile')}
          </button>

          <div className="border-t border-ink-100 mt-1 pt-1">
            <button
              onClick={() => { setOpen(false); logout(); navigate('/login', { replace: true }) }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-flame-600 hover:bg-flame-50 transition-colors"
            >
              <Icon name="log-out" className="w-4 h-4" />
              {t('user_menu.sign_out')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function Topbar() {
  const { currency, setCurrency } = useCurrency()
  const { learnMode, toggle } = useLearnMode()
  const { t, i18n } = useTranslation()
  const lang = i18n.language.startsWith('tr') ? 'tr' : 'en'

  return (
    <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-ink-200">
      <div className="h-16 px-5 flex items-center gap-4">
        <div className="flex items-center gap-3 w-64 shrink-0">
          <div className="w-9 h-9 rounded-xl gradient-hero grid place-items-center text-white font-bold">B</div>
          <div>
            <div className="text-[15px] font-bold leading-tight tracking-tight">
              ByeLabel<span className="text-mint-500">·</span><span className="text-ink-500 font-medium">FinOps</span>
            </div>
            <div className="text-[11px] text-ink-500">CFO Console</div>
          </div>
        </div>

        <div className="relative flex-1 max-w-2xl">
          <Icon name="search" className="w-4 h-4 absolute left-3 top-3 text-ink-400" />
          <input
            placeholder={t('topbar.search_placeholder')}
            className="w-full h-10 pl-9 pr-4 bg-ink-50 border border-ink-200 rounded-xl text-sm focus-ring placeholder:text-ink-400"
          />
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {/* Language switcher */}
          <div className="flex items-center gap-1 text-[12px]">
            <button
              onClick={() => i18n.changeLanguage('en')}
              className={`px-1.5 py-0.5 rounded ${lang === 'en' ? 'font-bold text-ink-900 bg-ink-100' : 'text-ink-400 hover:text-ink-600'}`}
            >
              EN
            </button>
            <span className="text-ink-300">|</span>
            <button
              onClick={() => i18n.changeLanguage('tr')}
              className={`px-1.5 py-0.5 rounded ${lang === 'tr' ? 'font-bold text-ink-900 bg-ink-100' : 'text-ink-400 hover:text-ink-600'}`}
            >
              TR
            </button>
          </div>

          {/* Learn mode toggle */}
          <button
            onClick={toggle}
            title={learnMode
              ? t('topbar.learn_mode_on')
              : t('topbar.learn_mode_off')}
            className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              learnMode
                ? 'bg-amber-50 border-amber-300 text-amber-800'
                : 'bg-ink-50 border-ink-200 text-ink-600 hover:bg-ink-100'
            }`}
          >
            <Icon name="book-open" className="w-4 h-4" />
            <span>{t('topbar.learn_mode_label', { state: learnMode ? t('topbar.learn_mode_on_state') : t('topbar.learn_mode_off_state') })}</span>
          </button>

          <div className="hidden md:flex items-center bg-ink-100 rounded-lg p-0.5 text-sm font-medium">
            {CURRENCIES.map(c => (
              <button
                key={c}
                onClick={() => setCurrency(c)}
                className={`px-3 py-1.5 rounded-md ${c === currency ? 'bg-white shadow-sm' : 'text-ink-500'}`}
              >
                {c}
              </button>
            ))}
          </div>

          <UserMenu />
        </div>
      </div>
    </header>
  )
}
